import { useEffect, useRef, useState } from "react";
import { Bot, Send, ThumbsUp, ThumbsDown, FileText, Loader2, Trash2, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { streamChat, getSession, nsKey } from "@/lib/assistantChat";
import { Button } from "@/components/ui/button";

export default function AssistantConsole({ onOpenLogs }) {
  const { user } = useAuth();
  const uid = user?.email || null;
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const scrollRef = useRef(null);
  const sessionId = useRef(null);

  useEffect(() => {
    if (!uid) return;
    try { setMessages(JSON.parse(localStorage.getItem(nsKey(uid, "msgs")) || "[]")); } catch { setMessages([]); }
    sessionId.current = getSession(uid);
  }, [uid]);

  useEffect(() => {
    if (uid) { try { localStorage.setItem(nsKey(uid, "msgs"), JSON.stringify(messages.slice(-40))); } catch { /* ignore */ } }
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, uid]);

  const setLast = (updater) =>
    setMessages((prev) => {
      const next = [...prev];
      const i = next.length - 1;
      next[i] = typeof updater === "function" ? updater(next[i]) : { ...next[i], ...updater };
      return next;
    });

  const send = async () => {
    const q = input.trim();
    if (!q || busy || !sessionId.current) return;
    setInput("");
    setBusy(true);
    setMessages((prev) => [...prev, { role: "user", text: q }, { role: "assistant", text: "", sources: [], logId: null, streaming: true }]);
    try {
      await streamChat({
        sessionId: sessionId.current,
        message: q,
        handlers: {
          onSources: (s) => setLast((m) => ({ ...m, sources: s })),
          onDelta: (t) => setLast((m) => ({ ...m, text: (m.text || "") + t })),
          onError: (msg) => setLast((m) => ({ ...m, text: (m.text || "") + "\n\n⚠️ " + msg })),
          onDone: (id) => setLast((m) => ({ ...m, logId: id, streaming: false })),
        },
      });
      setLast((m) => ({ ...m, streaming: false }));
    } catch {
      setLast((m) => ({ ...m, text: m.text || "Sorry, something went wrong. Please try again.", streaming: false }));
    } finally {
      setBusy(false);
    }
  };

  const vote = async (logId, rating, idx) => {
    if (!logId) return;
    try { await api.post("/assistant/feedback", { log_id: logId, rating }); setMessages((p) => p.map((m, i) => (i === idx ? { ...m, vote: rating } : m))); } catch { /* ignore */ }
  };

  const clearChat = () => { setMessages([]); if (uid) localStorage.removeItem(nsKey(uid, "msgs")); };

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col" style={{ height: "72vh" }} data-testid="assistant-console">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <div className="flex items-center gap-2 font-heading font-bold"><Bot className="w-5 h-5 text-primary" /> Velopaths AI Assistant</div>
          <div className="text-xs text-muted-foreground mt-0.5" data-testid="console-role-indicator">Role: <span className="font-semibold text-foreground">Admin</span> · read-only · test any question here</div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={clearChat} data-testid="console-clear"><Trash2 className="w-4 h-4 mr-1" />Clear</Button>
          <Button size="sm" onClick={onOpenLogs} data-testid="console-open-logs"><BarChart3 className="w-4 h-4 mr-1" />Review Assistant Logs</Button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="console-messages">
        {messages.length === 0 && (
          <div className="text-center text-sm text-muted-foreground mt-10">
            <Bot className="w-8 h-8 mx-auto mb-2 text-primary" />
            Ask anything partners or admins might ask — zones, billing, advertisers, routes, content.
            <div className="text-xs mt-1">Every question is logged so you can review weak answers under “Review Assistant Logs”.</div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={"max-w-[80%] rounded-2xl px-3 py-2 text-sm " + (m.role === "user" ? "bg-primary text-primary-foreground" : "bg-background border border-border")} data-testid={m.role === "user" ? "console-user-msg" : "console-reply-msg"}>
              <div className="whitespace-pre-wrap">{m.text}</div>
              {m.role === "assistant" && m.streaming && !m.text && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
              {m.role === "assistant" && m.sources?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {m.sources.map((s, j) => (
                    <span key={j} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px] border border-primary/20" title={s.path}><FileText className="w-3 h-3" /> {s.title}</span>
                  ))}
                </div>
              )}
              {m.role === "assistant" && m.logId && !m.streaming && (
                <div className="mt-2 flex items-center gap-2 text-muted-foreground">
                  <button onClick={() => vote(m.logId, "up", i)} className={"p-1 hover:text-emerald-400 " + (m.vote === "up" ? "text-emerald-400" : "")} data-testid="console-thumbs-up"><ThumbsUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => vote(m.logId, "down", i)} className={"p-1 hover:text-rose-400 " + (m.vote === "down" ? "text-rose-400" : "")} data-testid="console-thumbs-down"><ThumbsDown className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 border-t border-border px-4 py-3">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Ask about zones, billing, routes…" className="flex-1 rounded-full bg-background border border-border px-4 py-2 text-sm outline-none focus:border-primary" disabled={busy} data-testid="console-input" />
        <Button onClick={send} disabled={busy || !input.trim()} size="icon" className="rounded-full" data-testid="console-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
      </div>
    </div>
  );
}
